/**
 * The administration overview.
 *
 * Counts and the last few audit entries — deliberately small. A dashboard that
 * tries to be a report becomes a page nobody reads and a query nobody can
 * optimise; this one answers "is anything obviously wrong" and links to the
 * screens that answer everything else.
 *
 * Each panel is guarded by the permission its data needs, so an administrator
 * with partial rights sees a smaller dashboard rather than a broken one.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Can } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { Card, ErrorState, LoadingState, Page } from "@hardauto/core-ui";

import { AdminApi, type AuditEntry } from "../api";
import { NAMESPACE } from "../i18n";
import "./admin.css";

interface Counts {
  total: number;
  active: number;
  suspended: number;
  pending: number;
  roles: number;
}

export function DashboardScreen({ client, auditHref }: { client: ApiClient; auditHref?: string }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new AdminApi(client), [client]);

  const [counts, setCounts] = useState<Counts | null>(null);
  const [recent, setRecent] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      // Counted by asking for a single row of each status and reading `total`,
      // rather than fetching every user and counting in the browser. The
      // difference is one page versus the entire tenant.
      const [all, active, suspended, pending, roles] = await Promise.all([
        api.listUsers({ limit: 1 }),
        api.listUsers({ limit: 1, status: "active" }),
        api.listUsers({ limit: 1, status: "suspended" }),
        api.listUsers({ limit: 1, status: "pending_verification" }),
        api.listRoles(),
      ]);
      setCounts({
        total: all.total,
        active: active.total,
        suspended: suspended.total,
        pending: pending.total,
        roles: roles.roles.length,
      });

      try {
        const trail = await api.readAudit({ limit: 5 });
        setRecent(trail.entries);
      } catch {
        // The trail needs its own permission. Not being allowed to read it is
        // an ordinary state for some administrators, not a failure of the
        // dashboard — so the panel is simply absent.
        setRecent([]);
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !counts) return <LoadingState label={t("dashboard.loading")} />;
  if (failed)
    return (
      <ErrorState
        title={t("dashboard.loadFailed")}
        onRetry={() => void load()}
        retryLabel={t("error.retry")}
      />
    );

  return (
    <Page title={t("dashboard.title")} description={t("dashboard.description")}>
      <Can permission="users.profile.read">
        <div className="hardauto-admin-metrics">
          <Metric label={t("dashboard.users")} value={counts?.total ?? 0} />
          <Metric label={t("dashboard.active")} value={counts?.active ?? 0} />
          <Metric label={t("dashboard.pending")} value={counts?.pending ?? 0} />
          <Metric label={t("dashboard.suspended")} value={counts?.suspended ?? 0} />
        </div>
      </Can>

      {recent.length > 0 ? (
        <Card title={t("dashboard.recentActivity")}>
          <ul className="hardauto-admin-activity">
            {recent.map((entry) => (
              <li key={`${entry.occurred_at}-${entry.action}`}>
                <code>{entry.action}</code>{" "}
                <time dateTime={entry.occurred_at}>
                  {new Date(entry.occurred_at).toLocaleString()}
                </time>
              </li>
            ))}
          </ul>
          {auditHref ? <a href={auditHref}>{t("dashboard.viewAll")}</a> : null}
        </Card>
      ) : null}
    </Page>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="hardauto-admin-metric">
      {/* The number is announced with its label because they are in one
          element; a bare number in its own div reads as a stray digit. */}
      <p className="hardauto-admin-metric__value">{value}</p>
      <p className="hardauto-admin-metric__label">{label}</p>
    </div>
  );
}
